/**
 * Cookie Consent Banner
 *
 * Displays a cookie consent banner at the bottom of the page.
 * Uses localStorage to remember user preference.
 */

import { useState, useEffect } from 'react';
import { Box, Typography, Button, Slide, Link } from '@mui/material';
import { Cookie as CookieIcon } from '@mui/icons-material';

const COOKIE_CONSENT_KEY = 'lov_cookie_consent';

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined');
    setShowBanner(false);
  };

  return (
    <Slide direction="up" in={showBanner} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: '#112240',
          borderTop: '1px solid #233554',
          p: 2,
          zIndex: 1050,
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <CookieIcon sx={{ color: 'warning.main', fontSize: 32 }} />
            <Box>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                We use cookies to enhance your experience
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                By continuing to use this site, you agree to our use of cookies. Learn more in our{' '}
                <Link href="/privacy" sx={{ color: 'success.main' }}>
                  Privacy Policy
                </Link>
                .
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDecline}
              sx={{
                color: 'text.secondary',
                borderColor: '#233554',
                '&:hover': {
                  borderColor: 'success.main',
                  color: 'success.main',
                },
              }}
            >
              Decline
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleAccept}
              sx={{
                bgcolor: 'success.main',
                color: 'background.paper',
                '&:hover': {
                  bgcolor: '#4fd1c5',
                },
              }}
            >
              Accept
            </Button>
          </Box>
        </Box>
      </Box>
    </Slide>
  );
}
